import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Select, Btn, SectionHeader, AlertBanner } from '../components/UI';
import { fmtM, fmtDate, todayStr, SOCIOS, COLORS_SOCIOS, ini } from '../utils';
import { showToast } from '../components/UI';

export default function Socios({ store, setItem, setCol }) {
  const { socios, movimientos } = store;
  const [modal, setModal] = useState(null); // { socio, tipo }
  const [form, setForm] = useState({});

  // Build account for each socio
  const cuentas = useMemo(() => {
    return SOCIOS.map(nombre => {
      const socioData = Object.values(socios || {}).find(s => s.nombre === nombre) || { nombre, saldo: 0, movimientos: {} };
      const retiros = Object.values(movimientos || {}).filter(m => m.tipo === 'retiro' && m.socio === nombre);
      const totalRetiros = retiros.reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
      const saldo = parseFloat(socioData.saldo) || 0;
      return {
        nombre,
        saldo,
        totalRetiros,
        disponible: saldo - totalRetiros,
        color: COLORS_SOCIOS[nombre] || '#888',
        movs: Object.values(socioData.movimientos || {}).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')),
      };
    });
  }, [socios, movimientos]);

  function openRetiro(socio) {
    setForm({ monto: '', medio: 'efectivo', fecha: todayStr(), obs: '' });
    setModal({ socio, tipo: 'retiro' });
  }

  function openAjuste(socio) {
    setForm({ monto: '', signo: '+', motivo: '', fecha: todayStr() });
    setModal({ socio, tipo: 'ajuste' });
  }

  function saveRetiro() {
    const monto = parseFloat(form.monto);
    if (!monto || monto <= 0) { showToast('Ingresá un monto'); return; }
    const { socio } = modal;

    // Register as movement
    const movId = 'm' + Date.now();
    setItem('movimientos', movId, {
      id: movId, tipo: 'retiro', socio, fecha: form.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: form.medio, signo: '-',
      desc: `Retiro ${socio}${form.obs ? ' — ' + form.obs : ''}`,
      autor: 'panel'
    });

    // Deduct from socio account
    const socioData = Object.values(socios || {}).find(s => s.nombre === socio) || { nombre: socio, saldo: 0, movimientos: {} };
    const socioId = Object.keys(socios || {}).find(k => socios[k].nombre === socio) || ('s' + Date.now());
    const mid = 'r' + Date.now();
    setItem('socios', socioId, {
      ...socioData,
      movimientos: {
        ...socioData.movimientos,
        [mid]: { tipo: 'retiro', monto, medio: form.medio, fecha: form.fecha, obs: form.obs }
      }
    });

    showToast(`Retiro de ${fmtM(monto)} registrado para ${socio}`);
    setModal(null);
  }

  function saveAjuste() {
    const monto = parseFloat(form.monto);
    if (!monto || !form.motivo) { showToast('Ingresá monto y motivo'); return; }
    const { socio } = modal;
    const signo = form.signo === '+' ? 1 : -1;

    const socioData = Object.values(socios || {}).find(s => s.nombre === socio) || { nombre: socio, saldo: 0, movimientos: {} };
    const socioId = Object.keys(socios || {}).find(k => socios[k].nombre === socio) || ('s' + Date.now());
    const mid = 'aj' + Date.now();
    setItem('socios', socioId, {
      ...socioData,
      saldo: (parseFloat(socioData.saldo) || 0) + signo * monto,
      movimientos: {
        ...socioData.movimientos,
        [mid]: { tipo: 'ajuste', monto: signo * monto, fecha: form.fecha, obs: form.motivo }
      }
    });

    showToast(`Ajuste de ${fmtM(monto)} aplicado a ${socio}`);
    setModal(null);
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
        {cuentas.map(cuenta => (
          <div key={cuenta.nombre} style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: cuenta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {ini(cuenta.nombre)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{cuenta.nombre}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Cuenta de ganancias</div>
              </div>
            </div>

            {/* Balances */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>Acreditado</div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--grn)' }}>{fmtM(cuenta.saldo)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>Retirado</div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--red)' }}>{fmtM(cuenta.totalRetiros)}</div>
                </div>
              </div>
              <div style={{ background: 'var(--surf2)', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase' }}>Disponible</div>
                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Courier, monospace', color: cuenta.disponible >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                  {fmtM(cuenta.disponible)}
                </div>
              </div>
              {cuenta.disponible < 0 && (
                <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>⚠ Cuenta en negativo</div>
              )}
            </div>

            {/* Actions */}
            <div style={{ padding: '10px 16px', display: 'flex', gap: 6 }}>
              <Btn size="sm" variant="grn" onClick={() => openRetiro(cuenta.nombre)} style={{ flex: 1, justifyContent: 'center' }}>− Retiro</Btn>
              <Btn size="sm" onClick={() => openAjuste(cuenta.nombre)} style={{ flex: 1, justifyContent: 'center' }}>Ajuste</Btn>
            </div>

            {/* Last movements — both acreditaciones and retiros */}
            {(() => {
              const retirosSocio = Object.values(movimientos || {}).filter(m => m.tipo === 'retiro' && m.socio === cuenta.nombre).sort((a,b) => (b.fecha||'').localeCompare(a.fecha||'')).slice(0,3);
              const acredSocio = cuenta.movs.filter(m => m.tipo === 'acreditacion').slice(0,3);
              const combined = [...retirosSocio.map(m => ({...m, _cat:'retiro'})), ...acredSocio.map(m => ({...m, _cat:'acred'}))].sort((a,b) => (b.fecha||'').localeCompare(a.fecha||'')).slice(0,5);
              if (!combined.length) return null;
              return (
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <div style={{ padding: '8px 16px', fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>Últimos movimientos</div>
                  {combined.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderTop: '1px solid var(--border)', fontSize: 11 }}>
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 99, fontWeight: 700, background: m._cat === 'retiro' ? 'var(--red-l)' : 'var(--grn-l)', color: m._cat === 'retiro' ? 'var(--red)' : 'var(--grn)' }}>{m._cat === 'retiro' ? 'Retiro' : 'Acred.'}</span>
                      <div style={{ flex: 1, color: 'var(--tx2)' }}>{m.obs || m.semana || m.tipo}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}</div>
                      <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, color: m._cat === 'retiro' ? 'var(--red)' : 'var(--grn)' }}>{fmtM(Math.abs(m.monto))}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      {/* Resumen mensual de retiros */}
      <div style={{ marginTop: 8, marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>Retiros por mes</div>
        {(() => {
          const retiros = Object.values(movimientos || {}).filter(m => m.tipo === 'retiro' && m.fecha);
          if (!retiros.length) return (
            <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 30, textAlign: 'center', color: 'var(--tx3)', fontSize: 13 }}>
              Sin retiros registrados
            </div>
          );
          const meses = [...new Set(retiros.map(r => r.fecha.slice(0,7)))].sort().reverse();
          return (
            <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--surf2)' }}>
                    <th style={{ textAlign: 'left', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>Mes</th>
                    {SOCIOS.map(n => (
                      <th key={n} style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS_SOCIOS[n] }}></div>
                          <span style={{ color: 'var(--tx3)' }}>{n}</span>
                        </div>
                      </th>
                    ))}
                    <th style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>Total mes</th>
                  </tr>
                </thead>
                <tbody>
                  {meses.map((mes, i) => {
                    const retirosDelMes = retiros.filter(r => r.fecha.slice(0,7) === mes);
                    const porSocio = {};
                    SOCIOS.forEach(n => { porSocio[n] = retirosDelMes.filter(r => r.socio === n).reduce((a, r) => a + (parseFloat(r.monto)||0), 0); });
                    const totalMes = Object.values(porSocio).reduce((a, v) => a + v, 0);
                    return (
                      <tr key={mes} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '' : 'var(--surf2)' }}>
                        <td style={{ padding: '9px 14px', fontFamily: 'Courier, monospace', fontWeight: 700 }}>{mes}</td>
                        {SOCIOS.map(n => (
                          <td key={n} style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 600, color: porSocio[n] > 0 ? 'var(--red)' : 'var(--tx3)' }}>
                            {porSocio[n] > 0 ? fmtM(porSocio[n]) : '—'}
                          </td>
                        ))}
                        <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(totalMes)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: 'var(--surf2)', borderTop: '2px solid var(--border2)' }}>
                    <td style={{ padding: '9px 14px', fontWeight: 700 }}>TOTAL</td>
                    {SOCIOS.map(n => {
                      const tot = retiros.filter(r => r.socio === n).reduce((a, r) => a + (parseFloat(r.monto)||0), 0);
                      return <td key={n} style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(tot)}</td>;
                    })}
                    <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>
                      {fmtM(retiros.reduce((a, r) => a + (parseFloat(r.monto)||0), 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Acreditaciones por socio */}
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>Historial de acreditaciones por semana</div>
        {SOCIOS.map(nombre => {
          const socioData = Object.values(socios || {}).find(s => s.nombre === nombre);
          const acreds = Object.values(socioData?.movimientos || {}).filter(m => m.tipo === 'acreditacion').sort((a,b) => (b.fecha||'').localeCompare(a.fecha||''));
          if (!acreds.length) return null;
          return (
            <div key={nombre} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: COLORS_SOCIOS[nombre], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700 }}>{ini(nombre)}</div>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{nombre}</span>
                <span style={{ fontSize: 11, color: 'var(--tx3)' }}>— {fmtM(acreds.reduce((a,m) => a+(parseFloat(m.monto)||0), 0))} acreditado total</span>
              </div>
              <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: 'var(--surf2)' }}>
                      {['Fecha','Semana','Monto','Ajuste aplicado'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '7px 12px', fontSize: 9, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {acreds.slice(0, 12).map((m, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '7px 12px', fontFamily: 'Courier, monospace', fontSize: 10, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}</td>
                        <td style={{ padding: '7px 12px', fontSize: 10, color: 'var(--tx2)' }}>{m.semana || '—'}</td>
                        <td style={{ padding: '7px 12px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--grn)' }}>{fmtM(m.monto)}</td>
                        <td style={{ padding: '7px 12px', fontFamily: 'Courier, monospace', fontSize: 10, color: (m.ajuste||0) >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                          {Math.abs(m.ajuste||0) > 1 ? `${(m.ajuste||0) >= 0 ? '+' : ''}${fmtM(m.ajuste)}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Retiro modal */}
      {modal?.tipo === 'retiro' && (
        <Modal title={`Retiro — ${modal.socio}`} onClose={() => setModal(null)} onSave={saveRetiro} saveLabel="Registrar retiro">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Monto">
              <Input value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} type="number" placeholder="0" autoFocus />
            </Field>
            <Field label="Fecha">
              <Input value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Medio" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['efectivo', 'Efectivo'], ['transferencia', 'Transferencia']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({ ...f, medio: val }))}
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: `2px solid ${form.medio === val ? 'var(--grn)' : 'var(--border2)'}`, background: form.medio === val ? 'var(--grn-l)' : 'var(--surf2)', color: form.medio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Observaciones" full>
              <Input value={form.obs} onChange={v => setForm(f => ({ ...f, obs: v }))} placeholder="Opcional..." />
            </Field>
          </div>
        </Modal>
      )}

      {/* Ajuste modal */}
      {modal?.tipo === 'ajuste' && (
        <Modal title={`Ajuste manual — ${modal.socio}`} onClose={() => setModal(null)} onSave={saveAjuste} saveLabel="Aplicar ajuste">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Tipo de ajuste" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['+', 'Sumar (acreditar)'], ['-', 'Restar (debitar)']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({ ...f, signo: val }))}
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: `2px solid ${form.signo === val ? (val === '+' ? 'var(--grn)' : 'var(--red)') : 'var(--border2)'}`, background: form.signo === val ? (val === '+' ? 'var(--grn-l)' : 'var(--red-l)') : 'var(--surf2)', color: form.signo === val ? (val === '+' ? 'var(--grn)' : 'var(--red)') : 'var(--tx2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Monto">
              <Input value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} type="number" placeholder="0" autoFocus />
            </Field>
            <Field label="Fecha">
              <Input value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Motivo" full>
              <Input value={form.motivo} onChange={v => setForm(f => ({ ...f, motivo: v }))} placeholder="Ej: Ajuste semana anterior, compensación..." />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
